import {
  IListing,
  ICustomizations,
  IListingsAndConfig,
} from "./providers/types";
import { getCustomizations, loadCustomizations, getMainUrl } from "./providers/Customizations";
import { currencyToSymbol } from "./utils/price";
import { appendViewerHeaders, userLocation } from "./AdStack";
import { pushEventNow, pushCustomElementsNotSupported } from "./Events";
import {
  resolveLocalizedListings,
  resolveState,
  buildCacheUrl,
  everyNth,
  mergeEveryNth,
} from "./utils/localizedListings";
import { createElementSizeGate, measureElement } from "./utils/elementSizeGate";

const { OBSERVE_OFF, OBSERVE_ONCE } = process.env
export const prevWidths: {[id: string]: number | null} = {};
export function main(fn: () => any) {
  if ((window as any).sellwildInitialized) return
  (window as any)['sellwildInitialized'] = true
  try {
    fn();

    if (OBSERVE_OFF) return

    const observer = new MutationObserver(() => {
      const observed = fn()
      if (OBSERVE_ONCE && observed) {
        observer.disconnect()
      }
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  } catch (e) {
    const err = e as Error
    console.info(err.message);
  }
}

export function observeCustomElement (
  selector: string,
  onAdd: (element: Element, theme: ICustomizations, widgetId: string) => any,
  onRemove?: (element: Element, theme: ICustomizations, widgetId: string) => any,
) {
  if ((window as any)[`sellwild${selector}`]) return
  (window as any)[`sellwild${selector}`] = true
  let id = 0

  const attributes = {
    code: process.env.PARTNER_CODE || 'unknown',
  }

  function execute (element: HTMLElement) {
    id++
    const widgetId = String(id)
    const theme = getCustomizations(window.location, element);
    loadCustomizations(theme)
    // Promise.resolve()
    .then(() => {
      const added = onAdd(element, theme, widgetId)

      if (!theme.noResize) {
        // Only rebuild when the element's OWN box changed.
        //
        // `resize` is a window event, and a phone fires it while the reader is
        // simply SCROLLING -- the URL bar collapses and expands and the viewport
        // height goes with it. Rebuilding on that re-runs onAdd, which calls
        // googletag.defineSlot again and registers a SECOND slot for a space
        // that already has one, re-requests it, and repaints the creative in
        // front of the reader. Nothing destroys the first slot, so gamSlots
        // grows for the life of the page view and every later refresh walks a
        // longer array.
        //
        // Measured on the deployed sellwild bundle at a phone viewport: four ads
        // on the page, ONE dispatched resize took it to eight GAM slots, four
        // events to twenty, eight to thirty-six -- with the creatives visibly
        // reloading each time.
        //
        // The baseline is taken after onAdd, so the first render is not itself
        // read as a change. A real re-layout -- a column that narrowed, a
        // container revealed from display:none -- still rebuilds exactly as
        // before.
        const hasResized = createElementSizeGate(() => measureElement(element))
        hasResized()

        const resize = debounce(() => {
          if (!hasResized()) return
          onAdd(element, theme, widgetId)
        }, 1000)

        window.addEventListener('resize', resize)
      }

      return added
    })
    .catch((e) => {
      pushEventNow({ event: 'errorAddingElement', attributes, label: e.message })
    })
  }

  if ('customElements' in window) {
    customElements.define(selector, class extends HTMLElement {
      connectedCallback () {
        if (this.isConnected || !this.innerHTML) {
          execute(this)
        }
      }
      disconnectedCallback () {
        if (onRemove) {
          const theme = getCustomizations(window.location, this);
          onRemove(this, theme, String(id))
        }
      }
    })
  } else {
    pushCustomElementsNotSupported({ attributes })
    document.addEventListener('DOMContentLoaded', () => {
      // Find all instances of your custom element
      const elements = document.getElementsByTagName(selector);
      Array.from(elements).forEach(element => {
        execute(element as HTMLElement)
      });
    });
  }
}

export function observeNewElements(
  selector: string,
  fn: (element: Element, theme: ICustomizations, widgetId: string) => any
) {
  let observed = 0
  const elements = document.querySelectorAll(selector);
  for (var i in elements) {
    if (
      !elements[i] ||
      !elements[i].getAttribute ||
      (elements[i].getAttribute("observed") && elements[i].innerHTML)
      ) {
        // return;
      } else {
      observed++
      elements[i].setAttribute("observed", "yes");
      elements[i].innerHTML = '<span></span>';
      const theme = getCustomizations(window.location, elements[i]);
      delete prevWidths[i];
      if ((window as any).pbjs) {
        (window as any).pbjs.initAdserverSet = false
      }
      fn(elements[i], theme, i);
      var element = elements[i];
      if (!theme.noResize && typeof (window as any)["ResizeObserver"] !== "undefined") {
        var widgetId = i
        new (window as any)["ResizeObserver"](function () {
          const theme = getCustomizations(window.location, element);
          fn(element, theme, widgetId);
        }).observe(element);
      }
    }
  }
  return observed
}

export function times(n: number) {
  const a = [];
  for (let i = 0; i < n; i++) {
    a.push(i);
  }
  return a;
}

export type RemoteSession = { lotameId?: string, panoramaId?: string, userId?: string, widgetCacheVersionId?: string }
export async function createRemoteSession(session: RemoteSession) {
  const { lotameId, panoramaId, widgetCacheVersionId } = session

  if (!lotameId) return
  if (!panoramaId) return
  if (!widgetCacheVersionId) return

  const key = [lotameId, panoramaId, widgetCacheVersionId].join('_')
  const value = localStorage.getItem(key)

  if (value) {
    session.userId = value
    return value
  }

  const userId = await fetch("//api.sellwild.com/session/rpc", {
    method: "POST",
    body: JSON.stringify({
      method: "createRemoteSession",
      params: { trackingData: { lotameId, panoramaId }, widgetCacheVersionId },
      id: "0",
    }),
  })
  .then(v => v.json())
  .then(v => v && v.result && v.result.rs && v.result.rs.userId)
  
  if (userId) {
    session.userId = userId
    localStorage.setItem(key, userId)
  }

  return userId
}

export type ListingAction = { trackingData: { lotameId?: string, panoramaId?: string, userId?: string }, listingId: number, membershipType: string }
export function sendListingAction ({ trackingData, listingId, membershipType }: ListingAction) {
  return fetch("//api.sellwild.com/session/rpc", {
    method: "POST",
    body: JSON.stringify({
      method: "listingAction",
      params: { trackingData, listingId, membershipType: String(membershipType), actionType: '1' },
      id: "0",
    }),
  })
  .then(v => v.json())
}

const listingCache: {[uri:string]: Promise<IListingsAndConfig>|undefined} = {}

export function loadListingsSm(
  theme: ICustomizations
): Promise<IListingsAndConfig> {
  if (listingCache[theme.listings]) return listingCache[theme.listings] as Promise<IListingsAndConfig>

  const keywordsEl = (document.querySelector(theme.listingsTagCacheSelector || 'meta[name=keywords]') || document.querySelector('head>title')) as HTMLElement
  const keywordsContent = keywordsEl ? keywordsEl.getAttribute('content') || keywordsEl.textContent || '' : ''
  const keywords = keywordsContent.trim().slice(0, 100).replace(/[|]+/, '').split(',').map(v => v.trim()).join(',')
  const tagCache = keywords && theme.listingsTagCacheCount ? `https://tbd4rmdvjk.execute-api.us-east-1.amazonaws.com/dev/listings?keywords=${keywords}&count=${theme.listingsTagCacheCount}&v=1` : ''
  const tagCachePromise = tagCache
    ? fetch(tagCache).then((res) => res.json()).catch(() => [])
    : Promise.resolve([])

  const listingIds = new Set()
  const listings: IListing[] = []

  listingCache[theme.listings] = Promise.all([
    tagCachePromise,
    fetch(theme.listings).then(appendViewerHeaders)
  ])
  .then(([tagRes, res]) => {

    for (const listing of (tagRes || []).slice(0, theme.listingsTagCacheCount)) {
      listingIds.add(listing.id)
      listings.push(listing)
    }

    for (const listing of res.result.rs) {
      if (listingIds.has(listing.id)) continue
      listingIds.add(listing.id)
      listings.push(listing)
    }

    const base = {
      config: res.result.config,
      widgetCacheVersionId: res.result.widgetCacheVersionId || '0',
    }

    // Localized (geo-based) secondary listings: load a second cache keyed by the
    // user's state and disperse it into the primary feed at every Nth slot.
    // Runs inside the memoized promise so the merged result is what gets cached.
    const localized = resolveLocalizedListings(theme.localizedListings)
    if (localized) {
      const state = resolveState(localized, userLocation.state.code)
      const everyN = everyNth(localized.frequency)
      if (state && everyN > 0) {
        return fetch(buildCacheUrl(localized, state))
          .then((r) => r.json())
          .then((json) => {
            const secondary = (json && json.result && json.result.rs) || []
            return { listings: mergeEveryNth(listings, secondary, everyN), ...base }
          })
          .catch(() => ({ listings, ...base })) // a 404 (no data for state) is a normal skip
      }
    }

    return { listings, ...base }
  });

  return listingCache[theme.listings] as Promise<IListingsAndConfig>
}

export function computeCols(width: number, theme?: ICustomizations) {
  const breakpoints = theme && theme.breakpoints || {} as ICustomizations['breakpoints']
  if (width >= (breakpoints.col6 || 1800)) return 6;
  if (width >= (breakpoints.col5 || 1500)) return 5;
  if (width >= (breakpoints.col4 || 1200)) return 4;
  if (width >= (breakpoints.col3 || 900)) return 3;
  if (width >= (breakpoints.col2 || 600)) return 2;
  return 1;
}

export function searchListener () {
  const el = document.getElementById('sellwild-search')
  if (el) {
    el.addEventListener('submit', function (ev: any) {
      ev.preventDefault()
      const value = ev.target.q.value
      window.open(`https://sellwild.com/search?q=${value}&page=1`)
    })
  }
}

export function generateLiteWidget(
  theme: ICustomizations,
  topbanner: string,
  grid: string,
  bottombanner: string,
  cols: number
) {
  const title = (theme as any)[`title${cols}`] || theme.title;
  const linkText = (theme as any)[`linkText${cols}`] || theme.linkText;
  const titleHtml = [
    title || linkText ? '<div class="sellwild-header">' : "",
    title
      ? '<div class="sellwild-main-title">' +
        decodeURIComponent(title) +
        "</div><div style='width: 14px;'></div>"
      : "",
    linkText
      ? `<a class="sellwild-main-link" href="${getMainUrl(
          theme, 'sell', 'widget-header'
        )}" target="_blank" rel="nofollow sponsored noopener" aria-label="View all listings">` +
        decodeURIComponent(linkText) +
        "</a>"
      : "",
    title || linkText ? "</div>" : "",
  ].join("");
  return [
    `<div class="sellwild-widget ${
      theme.overlayTitle ? "sellwild-widget-overlay" : ""
    }">`,
    `${
      topbanner
        ? `<div class="sellwild-banner"><div class="sellwild-banner-ad">${topbanner}</div></div>`
        : ""
    }`,
    theme.titleBottom ? "" : titleHtml,
    buildSearch(theme, false),
    `<div class="sellwild-grid">${grid}</div>`,
    `${
      bottombanner
      ? `<div class="sellwild-banner"><div class="sellwild-banner-ad">${bottombanner}</div></div>`
      : ""
    }`,
    buildSearch(theme, true),
    theme.titleBottom ? titleHtml : "",
    buildFooter(theme),
    theme.watermark ? buildWatermark(theme, cols) : "",
    `</div>`,
    theme.fontUrl && theme.fontFamily
      ? `<link rel="preconnect" href="https://fonts.gstatic.com">
    <link href="${theme.fontUrl}" rel="stylesheet">`
      : "",
    `<style>${styles(theme)}</style>`,
  ].join("");
}

function buildSearch (theme: ICustomizations, bottom: boolean) {
  if (!theme.search) return ''
  if (bottom && !theme.searchBottom) return ''
  if (!bottom && theme.searchBottom) return ''
  const placeholder = decodeURIComponent(theme.placeholder) || 'Search items for sale'
  const search = decodeURIComponent(theme.search)
  return `<form id="sellwild-search">
    <input placeholder="${placeholder}" name="q" />
    <button type="submit">${search}</button>
  </form>`
}

function buildFooter (theme: ICustomizations) {
  if (!theme.footer) return ''
  return `<div class="sellwild-footer">${decodeURIComponent(theme.footer)}</div>`
}

function sellwildLogo () {
  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" id="Layer_1" viewBox="0 0 700 170"><script xmlns=""/><style>.st0{clip-path:url(#SVGID_2_);fill:url(#SVGID_3_)}</style><defs><path id="SVGID_1_" d="M107.7 120.6c0 26.7-23.9 45.3-51.1 45.3-20.3 0-39-8.4-54.6-22.6l19-19.8c8.6 9.5 21.8 16.6 35.6 16.6 12.9 0 22-8 22-19 0-19.4-28-21.8-48.3-33.6-11.9-7-21.4-17.4-21.4-34.4 0-27 24.8-42.5 49.8-42.5 15.3 0 29.6 3.7 43.8 14.2l-15 21.6c-6-5.6-16.2-9.9-26.1-9.9-11 0-23.3 4.7-23.3 16.4 0 24.3 69.6 16.8 69.6 67.7zm106-10.1c0 2.8-.2 5.6-.6 8h-72.5c1.5 14.7 13.4 24.8 28.3 24.8 11.9 0 21.4-6.7 26.3-12.1l16 16c-10.1 12.1-26.7 18.8-42.9 18.8-31.3 0-54.8-21.6-54.8-54.4 0-29.1 19.8-54.6 50.9-54.6 28.8 0 49.3 24.3 49.3 53.5zM185 97.9c-.9-9.3-9.3-18.3-20.7-18.3-11.6 0-21.1 8.8-22.4 18.3H185zm41.4 65.8h28V10.6h-28v153.1zm50.6 0h28V10.6h-28v153.1zm160.3-43.5l-26.1-61h-19.4l-26.3 61-25.5-61h-28.3l44 104.6h19.4l26.3-60.8 26.3 60.8h19.4l44-104.6H463l-25.7 61zm75-116.1C502.2 4.1 494 11.9 494 22c0 10.1 8.2 17.9 18.3 17.9 10.1 0 18.3-7.8 18.3-17.9 0-10.1-8.2-17.9-18.3-17.9zm-14.1 159.6h28V59.1h-28v104.6zm50.1 0h28V10.6h-28v153.1zM698 10.6v153.1h-27v-8.4c-8.6 7.3-18.5 10.6-29.1 10.6-15.5 0-30.2-6.9-39.5-17.7-8.2-9.5-13.4-22.4-13.4-36.7 0-30.8 22.9-54.6 51.3-54.6 11.4 0 21.4 3.2 29.6 9.7v-56H698zm-28.1 85.2c-5.8-7.8-15.1-12.9-25.9-12.9-16.4 0-27 13.6-27 28.7 0 16.4 11.9 28.5 27.6 28.5 10.6 0 19.8-5.2 25.2-12.9V95.8z"/></defs><use xlink:href="#SVGID_1_" overflow="visible" fill="#13284b"/><clipPath id="SVGID_2_"><use xlink:href="#SVGID_1_" overflow="visible"/></clipPath><radialGradient id="SVGID_3_" cx="530.894" cy="90.807" r="108.04" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#c7db4e"/><stop offset=".023" stop-color="#bcd74b"/><stop offset=".085" stop-color="#a3ce46"/><stop offset=".148" stop-color="#90c842"/><stop offset=".212" stop-color="#85c442"/><stop offset=".281" stop-color="#81c341"/><stop offset=".668" stop-color="#3eb54a"/><stop offset=".888" stop-color="#05a64b"/><stop offset="1" stop-color="#67bd45"/></radialGradient><path class="st0" d="M582.3 118.5c.3.2.7.4 1.1.6-3.3.7-9.5-.1-15.8-2-12.6-3.7-23.7-10.5-34.5-17.8-4.7-3.2-9.5-6.3-15-8-6-1.8-11.7-1.8-17.5 1-3 1.5-6.4 2.3-9.6 3.9.5 0 1 .1 1.4 0 6.7-1.2 13.1 0 19.1 3.2 2.8 1.5 5.6 3.1 8.2 5 12.4 9 25.9 16.1 40.3 21.1.3.1.6.3 1.2.5-7.9.5-15.1-1-22.2-3.1-9.4-2.7-18.1-6.9-26.6-11.6-7.9-4.4-15.9-8.6-24.8-10.9-3.4-.9-6.4-.7-9.7.8-18.4 8.4-35.8 18.4-51.5 31.2-.6.5-1.2 1-1.7 1.4 4.4-1 8.8-2.2 13.3-2.9 7.4-1.2 14.6.1 21.4 2.9 12.8 5.2 25.4 10.6 38.2 15.7 4.4 1.8 9.1 3.1 13.7 5H502c-15.4-.1-29.6-4.8-43.5-10.9-7.7-3.4-15.7-6.1-24.2-6.8-5-.4-9.9.3-14.7 1.9-.9.3-1.9 1-2.5 1.7-5.9 6.7-11.8 13.3-17.6 20.1-1.3 1.5-2.2 1.9-3.9.5.7-1 1.3-2 2.1-3 6.1-8.1 13.2-15.3 20.7-22.1 1.6-1.4 3-3.1 4.1-4.9 6.3-10.9 10.8-22.6 13.6-34.8 2.7-11.7 7.5-22.5 13.6-32.7 7-11.8 15.2-22.8 24.2-33.2-6.3 11.9-11.2 24.4-15.5 37.2-2.9 8.7-5.5 17.6-9 26.1-4.9 11.8-12.2 22.2-20.3 32-.6.7-1.2 1.5-1.8 2.2-.1.1-.1.3-.3.8.7-.4 1.2-.7 1.7-1 5.3-3.7 10.7-7.4 16-11.2 1.3-.9 2.6-2 3.4-3.3 6-10.5 12-21 17.7-31.7 11.6-21.8 25.5-41.9 41.6-60.6.2-.2.2-.6.8-.5-7.1 15.4-14 30.8-21.2 46.2-5.1 10.8-10.3 21.6-18.4 30.6-3.3 3.7-6.9 7.3-10.2 11.2 2.8-1.4 5.5-3.1 8.4-4.3 3.9-1.6 6.8-4.1 9.1-7.5 4.9-7.1 10.2-14.1 14.5-21.5 8.1-13.8 18.7-25.5 30.1-36.4 6.4-6.1 13.2-11.8 19.8-17.6.6-.6 1.3-1 2.3-1.8-13.8 19-27.2 37.7-40.8 56.2-6.3 8.5-14.2 15.5-22.7 21.7-.7.5-1.4 1.1-2 1.8 25.9-11.3 52.9-18.6 81.5-23-13.1 3.2-25.6 6.3-38 9.3 0 .2.1.4.1.6 2.7.8 5.5 1.3 8 2.6 6 3 11.9 6.3 17.8 9.5 11.8 7 23.8 13.8 35.8 20.6zm-175.8 28.3c8-10.3 13.2-22 17.2-34.2 6.8-21.4 15.3-42.2 25.6-62.2 1.5-2.9 3.1-5.9 4.7-8.8-.2-.1-.3-.3-.5-.4-.9 1-1.7 2.1-2.6 3-12 12.8-22.6 26.7-29.7 42.9-4.6 10.6-6.3 22-8.9 33.1-2.3 10.1-3.5 20.6-9.6 30.2.8-.5 1-.5 1.2-.7.8-.9 1.7-1.9 2.6-2.9zm45.8-25.8c-6.6 1.2-12.8 3.4-18.5 7 .8 0 1.6-.2 2.3-.4 16.4-4.5 31.9-3.1 46.9 5.4 5.1 2.9 10.7 5.2 16.2 7.1 11.1 3.9 22.8 4.2 34.7 5.3-14.4-6.7-28.3-13.7-42.6-19.7-12.3-5.3-25.5-7.1-39-4.7zm88.5-50.2c-5.2 3.8-10.9 7-16.4 10.5v.3c3.3-1 6.6-1.9 9.9-2.9 11.1-3.7 21.8-8 31-15.4 9.1-7.5 18.3-14.9 27.4-22.4l16.5-13.5c-1 0-1.8.2-2.6.6-6.1 2.9-12.3 5.6-18.2 8.7-10.5 5.4-20.6 11.4-29.4 19.4-5.7 5.1-11.8 10-18.2 14.7zm-37 42.5c-15.4-8.7-30.8-7.8-46.1.1 2.8-.2 5.5-.6 8.3-.6 11.4 0 21.9 3.4 32.2 7.8 9.5 4.1 19 8.1 28.7 11.6 6.1 2.2 12.7 2.7 19.5 1.7-.5-.3-.7-.5-.9-.6-14.3-5.6-28.2-12.4-41.7-20zm130.7-.4c-11.7-5.8-22.8-12.7-32.9-21.1-9.3-7.6-19.7-13-31.9-13.7-3.4-.2-6.9.4-10.3.6.1.1.1.2.2.4 3.4 1.3 7 2.2 10.2 4 5.1 2.9 10.2 5.9 14.6 9.7 10.9 9.3 23.4 15 37.1 18.4 4.6 1.1 9.3 1.9 13.9 2.9-.3-.9-.6-1.1-.9-1.2zM572.3 93c-6.7-4.7-13.8-8.9-21.9-10.7-5.1-1.2-10.3-1.3-15.8.3 2 .6 3.6 1 5.2 1.6 10.1 3.5 18.9 9.4 27.2 16.1 6.7 5.4 13.8 10.2 21.9 13.3 9.9 3.8 23.3 5.9 30.8 4.8-12.9-2.5-24.4-7.7-34.6-15.8-4.2-3.4-8.5-6.6-12.8-9.6zm-72.7-5.6c.1-.1.2-.1.4-.2 12.7-6.8 24.6-14.8 34.8-25.1 14.5-14.5 30.6-26.9 48.1-37.5.3-.2.5-.4.7-.6l-.3-.6C575.7 27 568 30.2 560.6 34c-11.7 5.9-22.6 13.2-31.8 22.7-9.9 10.1-19.5 20.4-29.2 30.7zm81.6-15.6c2.8.4 5 .9 7.3 1 11.9.5 23.9.8 35.8 1.2 9.5.3 19.1.7 28.6 1.2 4.4.2 8.7.9 13.1 1.3-13.6-11.5-60.9-14.3-84.8-4.7zm1.1-4.6c12.6-5 25.2-10.4 37.9-15.3 6.7-2.6 13.7-4.5 20.5-6.7-29.5 3.6-57.5 11.3-81.9 28.3 7.7-.9 15.7-3.2 23.5-6.3z"/></svg>`
}

function buildMainLink (theme: ICustomizations, children = '') {
  return `<a href=${getMainUrl(theme, 'post', 'watermark')} target="_blank" rel="nofollow sponsored noopener" aria-label="View all listings">${children}</a>`
}

function buildWatermark (theme: ICustomizations, cols: number) {
  const cs = []
  for (let i = 0; i < cols; i++) {
    cs.push(i)
  }
  return [
    '<div class="sellwild-watermark">',
    cs.map((i) => i === cs.length - 1 ? [
    '<div>',
    `<a href=${getMainUrl(theme, 'post', 'watermark')} target="_blank" rel="nofollow sponsored noopener" class="swlogo" aria-label="Powered by Sellwild"></a>`,
    '</div>',
    ].join('') : '<div></div>').join(''),
    '<div class="sellwild-popover">',
    `<a href=${getMainUrl(theme, 'post', 'watermark-popover')} target="_blank" rel="nofollow sponsored noopener" aria-label="Powered by Sellwild">`,
    decodeURIComponent(theme.watermarkTitle),
    '</a>',
    '</div>',
    '</div>',
  ].join('')
}

export function generateLiteListing(className = '') {
  return [
    `<a class="sellwild-listing${className ? ' ' + className : ''}" data-loading="true" rel="nofollow sponsored noopener" href="#">`,
    `<div class="listing-img-container" style="position: relative;">`,
    `<div class="listing-price"></div>`,
    `</div>`,
    `<div class="listing-overlay">`,
    `<p class="listing-title"></p>`,
    `<p class="listing-price-2"></p>`,
    `<div class="sellwild-buy-now"></div>`,
    `</div>`,
    `</a>`,
  ].join(" ");
}

const exclude = new Set([12, 13, 30, 14, 31])
function isShippable (listing: IListing | undefined) {
  return listing && Number(listing.price) < 4100 && Number(listing.shippable) && !exclude.has(Number(listing.categoryGroupId))
}

function isTrusted (listing: IListing | undefined) {
  return listing && listing.user && Number(listing.user.trustLevel) === 2
}
export function updateLiteListing(
  listingEl: Element,
  listing: IListing | undefined,
  href: string,
  theme: ICustomizations
) {
  var photo = listing && listing.photos && listing.photos[0] && listing.photos[0]
  var src = (photo && photo.url) || "";
  var bkg = (photo && photo.background) || "";
  var title = (listing && listing.title) || "";
  var url = href || (listing && listing.url);
  var isLotlinx = (listing && String(listing.dataSourceId) === '31');
  var isSella = (listing && listing.user && String(listing.user.id) === '95088975')
  var shippable = isShippable(listing);
  var trustedSeller = isTrusted(listing);
  var isSold = listing && listing.status == '5'
  if (title.length > 60) title = title.slice(0, 60) + "...";
  var currency = (listing && listing.currency) || "";
  var price =
    (listing &&
      listing.price &&
      !isNaN(Number(listing.price)) &&
      Number(listing.price).toLocaleString(undefined, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      })) ||
    "";
  var strikePrice =
    (listing &&
      listing.strikePrice &&
      !isNaN(Number(listing.strikePrice)) &&
      Number(listing.strikePrice).toLocaleString(undefined, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      })) ||
    "";
  const imgEl = document.createElement('img')
  imgEl.classList.add('listing-img');
  const imgContainer = listingEl.querySelector('.listing-img-container')
  const priceEl = listingEl.querySelector(`.listing-price`);
  const priceEl2 = listingEl.querySelector(`.listing-price-2`);
  const titleEl = listingEl.querySelector(`.listing-title`);
  const buyNowEl = listingEl.querySelector(`.sellwild-buy-now`);
  const userEl = listingEl.querySelector(`.listing-user`);
  if (listingEl) listingEl.setAttribute('target', '_blank');
  if (listingEl) listingEl.setAttribute("data-loading", "false");
  if (listing && listingEl) listingEl.setAttribute("data-id", listing.id);
  if (listingEl && url) listingEl.setAttribute("href", url);
  if (listingEl && isLotlinx) listingEl.setAttribute("data-partner", "lotlinx");
  else if (buyNowEl && url && !isSold && (isSella || (trustedSeller && shippable))) {
    (buyNowEl as any)['style']['visibility'] = 'visible'
    buyNowEl.textContent = String(listing.dataSourceId) === '30' ? 'Adopt' : (theme.buyNowText || 'Buy now');
    buyNowEl.addEventListener('click', (ev) => {
      ev.stopPropagation()
      window.open(url.replace('/product', '/checkout'))
    });
  }
  if (imgEl && src) imgEl.setAttribute("src", src);
  if (imgEl && title && title.trim()) {
    listingEl.setAttribute("aria-label", title)
    imgEl.setAttribute("alt", title)
  };
  if (imgEl && imgContainer && src) imgContainer.appendChild(imgEl)
  if (theme.imgBkg && imgContainer && bkg) (imgContainer as any)['style'].background = bkg
  if (priceEl && price && price !== '0') {
    if (strikePrice && strikePrice !== price) {
      priceEl.innerHTML = `<span class="strike-price">${currencyToSymbol(currency)}${strikePrice}</span> ${currencyToSymbol(currency)}${price}`;
    } else {
      priceEl.innerHTML = `${currencyToSymbol(currency)}${price}`;
    }
  }
  if (priceEl2 && price && price !== '0') {
    if (strikePrice && strikePrice !== price) {
      priceEl2.innerHTML = `<span class="strike-price">${currencyToSymbol(currency)}${strikePrice}</span> ${currencyToSymbol(currency)}${price}`;
    } else {
      priceEl2.innerHTML = `${currencyToSymbol(currency)}${price}`;
    }
  }
  else if (priceEl) {
    priceEl.setAttribute("style", "display: none");
  }
  if (titleEl && title) titleEl.innerHTML = title;
  if (userEl) {
    const lastname = listing && listing.user && listing.user.lastName ? `${listing.user.lastName.slice(0, 1)}.` : ''
    const username = listing && listing.user && listing.user.firstName ? `${listing.user.firstName} ${lastname}`.trim() : ''
    userEl.innerHTML = `${username ? `<span>${username}</span> | ` : ''}sellwild.com`
  }
}

export function styles(theme: ICustomizations) {
  return [
    "sellwild {display: block;}",
    `.sellwild-widget a,.sellwild-widget a:hover {text-decoration: none!important;color: inherit;}`,
    theme.fontFamily
      ? `.sellwild-widget {font-family: ${theme.fontFamily};}`
      : "",
    `.sellwild-widget {position: relative;width: 100%;display:block; isolation: isolate; font-size: ${theme.fontSize}px; }`,
    `.sellwild-header {margin-bottom: ${theme.marginBottom}px; position: relative;width: 100%;display:flex;flex-direction: row; justify-content: space-between; align-items: center; flex-wrap: wrap;}`,
    `.sellwild-main-title {white-space: nowrap;}`,
    `.sellwild-main-link {white-space: nowrap;}`,
    `.sellwild-widget a.sellwild-main-link { text-decoration: underline!important; }`,
    theme.titleColor
      ? `.sellwild-main-title { color: ${theme.titleColor} }`
      : "",
    theme.titleSize
      ? `.sellwild-main-title { font-size: ${theme.titleSize}px!important }`
      : "",
    theme.titleSize3
      ? `@media all and (max-width: 1200px) { .sellwild-main-title { font-size: ${theme.titleSize3}px!important } }`
      : "",
    theme.titleSize2
      ? `@media all and (max-width: 900px) { .sellwild-main-title { font-size: ${theme.titleSize2}px!important } }`
      : "",
    theme.titleSize1
      ? `@media all and (max-width: 600px) { .sellwild-main-title { font-size: ${theme.titleSize1}px!important } }`
      : "",
    theme.linkColor
      ? `.sellwild-widget a.sellwild-main-link { color: ${theme.linkColor} }`
      : "",
    theme.linkSize
      ? `.sellwild-widget a.sellwild-main-link { font-size: ${theme.linkSize}px }`
      : "",
    theme.linkSize3
      ? `@media all and (max-width: 1200px) { .sellwild-widget a.sellwild-main-link { font-size: ${theme.linkSize3}px } }`
      : "",
    theme.linkSize2
      ? `@media all and (max-width: 900px) { .sellwild-widget a.sellwild-main-link { font-size: ${theme.linkSize2}px } }`
      : "",
    theme.linkSize1
      ? `@media all and (max-width: 600px) { .sellwild-widget a.sellwild-main-link { font-size: ${theme.linkSize1}px } }`
      : "",
    `.sellwild-banner, .sellwild-banner-bottom {display: flex;justify-content: center;align-items: center;height: 90px; max-width: 100%; overflow: hidden; margin-bottom: ${theme.marginBottom}px;}`,
    `.sellwild-banner-bottom {align-items: flex-start;}`,
    `.sellwild-zip-ad {position: relative; background-color: #eee; width: 300px; height: 250px; margin-bottom: ${theme.marginBottom}px; display: flex; justify-content: center; align-items: center; background-size: cover}`,
    `.sellwild-grid {display: flex; flex-direction: row; flex-wrap: wrap; justify-content: space-around;overflow: hidden;}`,
    `.sellwild-listing {position: relative;width: ${theme.cardWidth}; height: 250px; color: inherit; margin-bottom: 1em; text-decoration: none;display: block;overflow:hidden; border-radius: 6px;}`,
    `.sellwild-listing-skinny{width: ${theme.skinnyWidth};}`,
    `.sellwild-clear {flex-basis: 100%;height: 0;}`,
    `.sellwild-listing-short{width: ${theme.shortWidth};height: ${theme.shortHeight};}`,
    `.sellwild-widget-overlay .sellwild-listing { margin-bottom: ${theme.marginBottom}px; }`,
    '.listing-img-container {width: 100%!important; height: 250px!important;}',
    `.listing-img {width: 100%!important; height: 250px!important; object-fit: cover; }`,
    `.sellwild-listing-short .listing-img-container { height: ${theme.shortHeight}!important; }`,
    `.sellwild-listing-short .listing-img { height: ${theme.shortHeight}!important; }`,
    `.sellwild-slider {display: flex; white-space: nowrap; overflow: scroll; max-width: 300px; scroll-snap-type: x mandatory;}`,
    `.sellwild-slider .sellwild-listing {min-width: 250px; margin-right: 10px; scroll-snap-align: start;}`,
    `.listing-overlay {position: absolute;transition: visibility 0s linear 0.3s, opacity 0.3s ease-in;opacity: 0;color: ${theme.fontColor};top: 0;bottom: 0;left: 0;right: 0;z-index: 3;display: flex;flex-direction: column; justify-content: center; align-items: center; grid-gap: 8px; padding: 4px; text-align: center;}`,
    `.listing-overlay::before {content: ""; position: absolute; top: 0; bottom: 0; left: 0; right: 0; background-color: ${theme.colors[0]}; opacity: 0.5; }`,
    `.listing-price {position: absolute;bottom: 0;right: 0;color: ${
      theme.priceFontColor || theme.fontColor
    };line-height: 2em;padding: 0px 18px;background-color: ${
      theme.priceColor || theme.colors[0]
    };text-decoration: none;z-index: 2; font-size: ${theme.fontSize}px;opacity: 1;visibility: visible;transition: visibility 0s linear, opacity 0.3s ease-in;}`,
    `.listing-title {position: relative; margin: 0; font-size: ${theme.fontSize}px!important; color: ${theme.fontColor}!important; padding-top: 0px; margin-top: 0!important; margin-bottom: 0!important; white-space: normal;}`,
    `.listing-price-2 {position: relative; margin: 0; font-size: ${theme.fontSize}px!important; color: ${theme.fontColor}!important; margin-top: 0!important; margin-bottom: 0!important;}`,
    `.sellwild-buy-now { position: relative; margin: 0; padding: 8px 16px; border: 2px solid; border-radius: 4px; background-color: rgba(255, 255, 255, 0); transition: background-color 0.3s ease-in; visibility: hidden;}`,
    `.sellwild-buy-now::after { content: ""; }`,
    `.sellwild-buy-now:hover,.sellwild-buy-now:active { background-color: rgba(255, 255, 255, 0.3); }`,
    `.sellwild-listing-short .listing-title {font-size: ${Math.floor(theme.fontSize*0.75)}px; padding-top: 10px;}`,
    `.sellwild-listing-short .listing-price {line-height: 1.2em;padding: 0px 9px;font-size: ${Math.floor(theme.fontSize*0.75)}px;}`,
    `.sellwild-listing-short .listing-price-2 {font-size: ${Math.floor(theme.fontSize*0.75)}px;}`,
    `.sellwild-listing-short .sellwild-buy-now {font-size: ${Math.floor(theme.fontSize*0.75)}px; padding: 2px 8px;}`,
    `.sellwild-widget-overlay a:hover .listing-overlay {opacity: 1;visibility: visible;transition: visibility 0s linear, opacity 0.3s ease-in;}`,
    `.sellwild-widget-overlay a:hover .listing-price {opacity: 0;visibility: hidden;transition: visibility 0s linear 0.3s, opacity 0.3s ease-in;}`,
    `.sellwild-widget-overlay a:hover .listing-title {opacity: 1;visibility: visible;transition: visibility 0s linear, opacity 0.3s ease-in;}`,
    `.sellwild-banner-ad {background-color: #eee; width: 728px; max-width: 100%; height: 90px;}`,
    `.sellwild-listing-ad {position: relative; background-color: #eee; width: 300px; height: 250px; margin-bottom: ${theme.marginBottom}px;}`,
    `.sellwild-listing-ad-skyscraper {width: 300px; margin-bottom: ${theme.marginBottom}px; display: flex; justify-content: center; align-items: center;}`,
    `.sellwild-watermark {display: flex; flex-direction: row; flex-wrap: wrap; justify-content: space-around;}`,
    `.sellwild-watermark div {width: 300px; display: flex; justify-content: flex-end;}`,
    '.sellwild-watermark .swlogo { display: block; height: 34px; width: 34px; background-image: url(https://widget.sellwild.com/sLogo.svg); background-size: contain; background-repeat: no-repeat; margin-left: 0.5rem; filter: grayscale(1);opacity: 0.5;transition: all 0.2s ease-in;}',
    '.sellwild-watermark:hover .swlogo { filter: grayscale(0);opacity: 1;}',
    `.sellwild-popover {opacity: 0; visibility: hidden; position: absolute; transform: translate(0, 10px); right: 100px; background-color: #ffffff; padding: 1.5rem; box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.26); width: auto!important; font-size: 14px;}`,
    `.sellwild-watermark:hover .sellwild-popover {z-index: 10; opacity: 1; visibility: visible; transform: translate(0, -20px); transition: all 0.5s cubic-bezier(0.75, -0.02, 0.2, 0.97);}`,
    `.sellwild-watermark .sellwild-popover a {color: ${theme.titleColor}!important;}`,
    `.sellwild-footer {display: flex; justify-content: center; align-items: center;}`,
    `.sellwild-footer svg {height: 1em; margin-left: 4px; filter: grayscale(1) brightness(4); margin-bottom: 5px;}`,
    `#sellwild-search {display: flex; justify-content: stretch; align-items: center; flex-direction: row; margin-bottom: ${theme.marginBottom}px; max-width: 100%; width: 100%;}`,
    `#sellwild-search input { flex: 1; color: ${theme.titleColor}; padding: 0px 16px; border: 1px solid ${theme.priceColor || theme.colors[0]}; border-radius: 12px 0 0 12px; height: 50px; border-right: 0; outline-color: ${theme.priceColor || theme.colors[0]}; box-sizing: border-box; margin: 0; width: 100%; }`,
    `#sellwild-search button { height: 50px; padding: 0 24px; color: ${theme.priceFontColor || theme.fontColor}; background-color: ${theme.priceColor || theme.colors[0]}; border-radius: 0 12px 12px 0; border: none; display: flex; justify-content: center; align-items: center; outline-color: ${theme.priceColor || theme.colors[0]}; box-sizing: border-box; margin: 0;}`,
    `.strike-price { text-decoration: line-through; margin-right: 8px; color: var(--text-color); }`,
    theme.css,
  ].join(" ");
}


export function afterInteraction(fn: () => any) {
  document.addEventListener("mousemove", fn);
  document.addEventListener("touchstart", fn);
  return () => {
    document.removeEventListener("mousemove", fn);
    document.removeEventListener("touchstart", fn);
  };
}

function debounce(fn: (...args: any[]) => any, n = 0) {
  let timerId: any
  return (...args: any[]) => {
    clearTimeout(timerId)
    timerId = setTimeout(() => {
      fn(...args)
    }, n)
  }
}